import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Send, Phone, CheckCircle, ArrowRight, Loader2, PhoneCall, PhoneOff, Mic } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import axios from "axios";
import { AVA } from "@/constants/testIds";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AVA_GREETING = "Hi, I'm AvA with Apexum Partners. I help qualified clinics connect with trusted clinical access pathways. I'll ask a few questions to understand your clinic model and route you appropriately.\n\nLet's start — what's your first name?";

function ChatBubble({ message, isStreaming }) {
  const isAvA = message.role === "assistant";
  const displayContent = message.content.replace(/<INTAKE_COMPLETE>[\s\S]*/g, "").trim();
  if (!displayContent && !isStreaming) return null;

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
      className={`flex ${isAvA ? "justify-start" : "justify-end"} mb-4`}>
      {isAvA && (
        <div className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0 mr-3 mt-1 ring-2 ring-blue-100">
          <img src="https://customer-assets.emergentagent.com/job_app-constructor-85/artifacts/ngv4sm7y_AvA%20Headshot.png"
            alt="AvA" className="w-full h-full object-cover object-top" />
        </div>
      )}
      <div className={`max-w-[80%] px-4 py-3 rounded-2xl text-sm leading-relaxed ${
        isAvA
          ? "bg-white border border-slate-100 shadow-sm text-slate-800 rounded-tl-sm"
          : "bg-blue-600 text-white rounded-tr-sm"
      }`}>
        {displayContent || (isStreaming ? "" : "")}
        {isStreaming && !displayContent && (
          <div className="flex gap-1 items-center py-0.5">
            <span className="typing-dot w-2 h-2 rounded-full bg-slate-400" />
            <span className="typing-dot w-2 h-2 rounded-full bg-slate-400" />
            <span className="typing-dot w-2 h-2 rounded-full bg-slate-400" />
          </div>
        )}
        {isStreaming && displayContent && (
          <span className="inline-block w-0.5 h-4 bg-blue-400 ml-0.5 animate-pulse align-middle" />
        )}
      </div>
    </motion.div>
  );
}

function CallNowForm() {
  const [phone, setPhone] = useState("");
  const [firstName, setFirstName] = useState("");
  const [status, setStatus] = useState("idle"); // idle | calling | called | error
  const [error, setError] = useState("");

  const handleCall = async (e) => {
    e.preventDefault();
    const digits = phone.replace(/\D/g, "");
    if (!firstName.trim()) { setError("Please enter your first name."); return; }
    if (digits.length < 10) { setError("Please enter a valid 10-digit phone number."); return; }
    setError("");
    setStatus("calling");
    try {
      await axios.post(`${API}/retell/call-request`, {
        first_name: firstName.trim(),
        phone: phone.trim(),
        best_time: "Right Now",
      });
      setStatus("called");
    } catch (e) {
      setError(e?.response?.data?.detail || "Something went wrong. Please try again.");
      setStatus("error");
    }
  };

  if (status === "called") {
    return (
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
        className="text-center py-6">
        <div className="relative w-24 h-24 mx-auto mb-5">
          <div className="absolute inset-0 rounded-full bg-emerald-400/20 animate-ping" />
          <div className="w-24 h-24 rounded-full p-[3px] bg-gradient-to-br from-blue-500 to-cyan-400 shadow-xl shadow-blue-500/30 relative">
            <div className="w-full h-full rounded-full overflow-hidden bg-white">
              <img src="https://customer-assets.emergentagent.com/job_app-constructor-85/artifacts/ngv4sm7y_AvA%20Headshot.png"
                alt="AvA" className="w-full h-full object-cover object-top" />
            </div>
          </div>
          <div className="absolute -bottom-1 -right-1 w-8 h-8 rounded-full bg-emerald-500 border-2 border-white flex items-center justify-center">
            <Phone size={15} className="text-white" />
          </div>
        </div>
        <h3 className="text-xl font-semibold text-slate-900 mb-1.5" style={{ fontFamily: 'Outfit, sans-serif' }}>
          AvA is calling you now
        </h3>
        <p className="text-slate-500 text-sm max-w-xs mx-auto mb-1">
          Answer the call from{" "}
          <span className="font-semibold text-blue-600">+1 (702) 983-8016</span>
        </p>
        <p className="text-slate-400 text-xs">
          AvA will guide you through your clinic intake by phone.
        </p>
        <button onClick={() => { setStatus("idle"); setPhone(""); setFirstName(""); }}
          className="mt-5 text-xs text-slate-400 underline underline-offset-2 hover:text-slate-600">
          Try a different number
        </button>
      </motion.div>
    );
  }

  return (
    <form onSubmit={handleCall} className="space-y-4">
      <div>
        <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wide">First Name</label>
        <input
          value={firstName}
          onChange={e => setFirstName(e.target.value)}
          className="w-full px-4 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 transition"
          placeholder="Sarah"
        />
      </div>
      <div>
        <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wide">Phone Number</label>
        <input
          type="tel"
          value={phone}
          onChange={e => setPhone(e.target.value)}
          className="w-full px-4 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 transition"
          placeholder="+1 (555) 000-0000"
        />
      </div>
      {error && <p className="text-sm text-red-500">{error}</p>}
      <button
        type="submit"
        disabled={status === "calling"}
        data-testid="ava-call-now-button"
        className="w-full flex items-center justify-center gap-2.5 bg-gradient-to-r from-blue-600 to-cyan-500 text-white py-3.5 rounded-full font-semibold hover:opacity-90 transition-all duration-200 disabled:opacity-60 shadow-lg shadow-blue-600/25 mt-1 text-sm">
        {status === "calling"
          ? <><Loader2 size={18} className="animate-spin" /> Connecting...</>
          : <><PhoneCall size={18} /> Have AvA Call Me Now</>}
      </button>
      <p className="text-xs text-center text-slate-400">
        AvA will call your phone from <span className="font-medium text-slate-500">+1 (702) 983-8016</span>
      </p>
    </form>
  );
}

function CallRequestForm() {
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", phone: "", best_time: "", company_name: "" });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.first_name || !form.phone || !form.email) {
      setError("Please fill in your name, phone, and email.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      await axios.post(`${API}/retell/call-request`, form);
      setSubmitted(true);
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
        className="text-center py-12">
        <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
          <CheckCircle size={28} className="text-emerald-500" />
        </div>
        <h3 className="text-xl font-semibold text-slate-900 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Call Request Received
        </h3>
        <p className="text-slate-500">
          AvA will call you at {form.phone} around {form.best_time}. The Apexum team will follow up if your organization is a fit.
        </p>
      </motion.div>
    );
  }

  return (
    <form data-testid={AVA.callRequestForm} onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1.5">First Name *</label>
          <input value={form.first_name} onChange={e => setForm({ ...form, first_name: e.target.value })}
            className="w-full px-4 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400"
            placeholder="Sarah" />
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1.5">Last Name</label>
          <input value={form.last_name} onChange={e => setForm({ ...form, last_name: e.target.value })}
            className="w-full px-4 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400"
            placeholder="Johnson" />
        </div>
      </div>
      <div>
        <label className="block text-xs font-medium text-slate-600 mb-1.5">Email Address *</label>
        <input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })}
          className="w-full px-4 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400"
          placeholder="JonDoe@ApexAva.ai" />
      </div>
      <div>
        <label className="block text-xs font-medium text-slate-600 mb-1.5">Phone Number *</label>
        <input type="tel" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })}
          className="w-full px-4 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400"
          placeholder="+1 (555) 000-0000" />
      </div>
      <div>
        <label className="block text-xs font-medium text-slate-600 mb-1.5">Clinic / Company Name</label>
        <input value={form.company_name} onChange={e => setForm({ ...form, company_name: e.target.value })}
          className="w-full px-4 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400"
          placeholder="Your Clinic Name" />
      </div>
      <div>
        <label className="block text-xs font-medium text-slate-600 mb-1.5">Best Time to Call *</label>
        <select value={form.best_time} onChange={e => setForm({ ...form, best_time: e.target.value })}
          className="w-full px-4 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 bg-white">
          <option value="">Select a time</option>
          <option>Morning (9am-12pm)</option>
          <option>Afternoon (12pm-3pm)</option>
          <option>Late Afternoon (3pm-5pm)</option>
          <option>Evening (5pm-7pm)</option>
        </select>
      </div>
      {error && <p className="text-sm text-red-500">{error}</p>}
      <button type="submit" disabled={loading}
        className="w-full flex items-center justify-center gap-2 bg-blue-600 text-white py-3 rounded-full font-medium hover:bg-blue-700 transition-all duration-200 disabled:opacity-50">
        {loading ? <Loader2 size={18} className="animate-spin" /> : <Phone size={18} />}
        {loading ? "Submitting..." : "Request a Call from AvA"}
      </button>
      <p className="text-xs text-center text-slate-400">
        AvA will call you from +1 (702) 983-8016
      </p>
    </form>
  );
}

export default function TalkToAvA() {
  const [activeTab, setActiveTab] = useState("online");
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [intakeComplete, setIntakeComplete] = useState(false);
  const [leadStatus, setLeadStatus] = useState("");
  const [sessionStarted, setSessionStarted] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });

  useEffect(() => { scrollToBottom(); }, [messages]);

  const startSession = async () => {
    try {
      const res = await axios.post(`${API}/ava/session`);
      const sid = res.data.session_id;
      setSessionId(sid);
      setSessionStarted(true);
      setMessages([{ role: "assistant", content: AVA_GREETING }]);
    } catch {
      setMessages([{ role: "assistant", content: AVA_GREETING }]);
      setSessionStarted(true);
      setSessionId("local_" + Date.now());
    }
  };

  const completeIntake = async (sid) => {
    try {
      const res = await axios.post(`${API}/ava/complete/${sid}`);
      if (res.data.success) {
        setIntakeComplete(true);
        setLeadStatus(res.data.lead_status);
      }
    } catch (e) {
      console.error("Complete intake error", e);
    }
  };

  const sendMessage = async () => {
    const msg = inputValue.trim();
    if (!msg || isTyping || !sessionId) return;
    setInputValue("");
    setIsTyping(true);

    const userMsg = { role: "user", content: msg };
    setMessages(prev => [...prev, userMsg, { role: "assistant", content: "", isStreaming: true }]);

    let avaResponse = "";

    try {
      const response = await fetch(`${API}/ava/chat/${sessionId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: msg }),
      });

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() || "";

        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const raw = line.slice(6).trim();
          if (!raw) continue;
          try {
            const data = JSON.parse(raw);
            if (data.type === "delta") {
              avaResponse += data.content;
              const display = avaResponse.replace(/<INTAKE_COMPLETE>[\s\S]*/g, "").trim();
              setMessages(prev => {
                const updated = [...prev];
                updated[updated.length - 1] = { role: "assistant", content: display, isStreaming: true };
                return updated;
              });
            } else if (data.type === "done") {
              const display = avaResponse.replace(/<INTAKE_COMPLETE>[\s\S]*/g, "").trim();
              setMessages(prev => {
                const updated = [...prev];
                updated[updated.length - 1] = { role: "assistant", content: display, isStreaming: false };
                return updated;
              });
              if (data.intake_complete) {
                await completeIntake(sessionId);
              }
            } else if (data.type === "error") {
              setMessages(prev => {
                const updated = [...prev];
                updated[updated.length - 1] = { role: "assistant", content: data.content || "Something went wrong. Please try again.", isStreaming: false };
                return updated;
              });
            }
          } catch {}
        }
      }
    } catch {
      setMessages(prev => {
        const updated = [...prev];
        updated[updated.length - 1] = { role: "assistant", content: "I'm having trouble connecting. Please try again.", isStreaming: false };
        return updated;
      });
    } finally {
      setIsTyping(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero */}
      <section className="pt-28 pb-10 bg-gradient-to-b from-blue-50 to-white">
        <div className="max-w-3xl mx-auto px-6 text-center">

          {/* AvA Profile Card */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="flex flex-col items-center mb-7">
            <div className="relative mb-4">
              {/* Gradient ring */}
              <div className="w-28 h-28 rounded-full p-[3px] bg-gradient-to-br from-blue-500 via-cyan-400 to-teal-400 shadow-2xl shadow-blue-500/25">
                <div className="w-full h-full rounded-full overflow-hidden bg-white">
                  <img
                    src="https://customer-assets.emergentagent.com/job_app-constructor-85/artifacts/ngv4sm7y_AvA%20Headshot.png"
                    alt="AvA Sterling — Clinical Access Connector"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>
              {/* Online indicator */}
              <div className="absolute bottom-0.5 right-0.5 w-5 h-5 rounded-full bg-emerald-400 border-2 border-white shadow-sm" />
            </div>
            <h3 className="text-xl font-semibold text-slate-900 mb-0.5" style={{ fontFamily: 'Outfit, sans-serif' }}>AvA Sterling</h3>
            <p className="text-sm text-slate-500">Clinical Access Connector · Apexum Partners</p>
            <p className="text-xs text-blue-500 mt-1.5 font-medium tracking-wide">
              AvA — Apexum's Verified Access
            </p>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
            <div className="inline-flex items-center gap-2 bg-blue-50 text-blue-700 px-4 py-1.5 rounded-full text-xs font-semibold mb-4 border border-blue-100">
              AI-Powered Clinical Access Connector
            </div>
            <h1 className="text-4xl sm:text-5xl font-medium text-slate-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Talk to <span className="gradient-text">AvA</span>
            </h1>
            <p className="text-lg text-slate-600">
              Complete your clinic intake online with AvA, or request a call from AvA directly.
            </p>
          </motion.div>

        </div>
      </section>

      {/* Tab Selector */}
      <section className="py-8">
        <div className="max-w-2xl mx-auto px-6">
          <div className="flex bg-slate-100 rounded-full p-1 gap-1">
            <button data-testid={AVA.tabOnline}
              onClick={() => setActiveTab("online")}
              className={`flex-1 py-2.5 rounded-full text-sm font-medium transition-all duration-200 ${
                activeTab === "online" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"
              }`}>
              Complete Intake Online
            </button>
            <button
              data-testid="ava-tab-call-now"
              onClick={() => setActiveTab("now")}
              className={`flex-1 py-2.5 rounded-full text-sm font-medium transition-all duration-200 ${
                activeTab === "now"
                  ? "bg-gradient-to-r from-blue-600 to-cyan-500 text-white shadow-sm"
                  : "text-slate-500 hover:text-slate-700"
              }`}>
              Have AvA Call You Now
            </button>
            <button data-testid={AVA.tabCall}
              onClick={() => setActiveTab("call")}
              className={`flex-1 py-2.5 rounded-full text-sm font-medium transition-all duration-200 ${
                activeTab === "call" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"
              }`}>
              Request a Call from AvA
            </button>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="pb-20">
        <div className="max-w-2xl mx-auto px-6">
          <AnimatePresence mode="wait">
            {activeTab === "online" ? (
              <motion.div key="online" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}>

                {intakeComplete ? (
                  <motion.div data-testid={AVA.completionScreen}
                    initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
                    className="bg-white rounded-3xl border border-slate-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] p-10 text-center">
                    <div className="w-20 h-20 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-6">
                      <CheckCircle size={36} className="text-emerald-500" />
                    </div>
                    <h2 className="text-2xl font-semibold text-slate-900 mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
                      Intake Complete
                    </h2>
                    <p className="text-slate-600 mb-2">
                      AvA has captured your clinic profile.
                    </p>
                    {leadStatus && (
                      <div className={`inline-block px-4 py-1.5 rounded-full text-sm font-medium mt-2 mb-4 ${
                        leadStatus === "Qualified Clinic" ? "bg-emerald-50 text-emerald-700 border border-emerald-100" :
                        leadStatus === "Missing Provider" ? "bg-amber-50 text-amber-700 border border-amber-100" :
                        "bg-blue-50 text-blue-700 border border-blue-100"
                      }`}>
                        Status: {leadStatus}
                      </div>
                    )}
                    <p className="text-sm text-slate-500 mt-4 max-w-sm mx-auto">
                      If your organization appears to be a fit, the Apexum team will follow up with the appropriate pathway.
                    </p>
                  </motion.div>
                ) : !sessionStarted ? (
                  <div className="bg-white rounded-3xl border border-slate-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] p-10 text-center">
                    <div className="relative w-20 h-20 mx-auto mb-6">
                      <div className="w-20 h-20 rounded-full p-[2px] bg-gradient-to-br from-blue-500 to-cyan-400 shadow-lg shadow-blue-500/20">
                        <div className="w-full h-full rounded-full overflow-hidden bg-white">
                          <img src="https://customer-assets.emergentagent.com/job_app-constructor-85/artifacts/ngv4sm7y_AvA%20Headshot.png"
                            alt="AvA" className="w-full h-full object-cover object-top" />
                        </div>
                      </div>
                      <div className="absolute bottom-0.5 right-0.5 w-4 h-4 rounded-full bg-emerald-400 border-2 border-white" />
                    </div>
                    <h2 className="text-2xl font-semibold text-slate-900 mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
                      Ready to get started?
                    </h2>
                    <p className="text-slate-500 mb-8 max-w-sm mx-auto text-sm">
                      AvA will guide you through a brief intake to understand your clinic's needs and find the right pathway.
                    </p>
                    <button onClick={startSession}
                      className="inline-flex items-center gap-2 bg-blue-600 text-white px-8 py-3.5 rounded-full font-medium hover:bg-blue-700 transition-all duration-200 shadow-lg shadow-blue-600/25">
                      Start Intake with AvA
                      <ArrowRight size={18} />
                    </button>
                  </div>
                ) : (
                  <div data-testid={AVA.chatContainer}
                    className="bg-white rounded-3xl border border-slate-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] overflow-hidden">
                    {/* Chat header */}
                    <div className="bg-gradient-to-r from-blue-600 to-cyan-500 px-6 py-4 flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full overflow-hidden ring-2 ring-white/30">
                        <img src="https://customer-assets.emergentagent.com/job_app-constructor-85/artifacts/ngv4sm7y_AvA%20Headshot.png"
                          alt="AvA Sterling" className="w-full h-full object-cover object-top" />
                      </div>
                      <div>
                        <div className="text-white font-semibold">AvA Sterling</div>
                        <div className="flex items-center gap-1.5">
                          <div className="w-2 h-2 rounded-full bg-emerald-400" />
                          <span className="text-white/80 text-xs">Clinical Access Connector · Apexum Partners</span>
                        </div>
                      </div>
                    </div>

                    {/* Messages */}
                    <div data-testid={AVA.messages}
                      className="h-[420px] overflow-y-auto p-6 bg-slate-50 space-y-1">
                      {messages.map((msg, i) => (
                        <ChatBubble key={i} message={msg} isStreaming={msg.isStreaming} />
                      ))}
                      <div ref={messagesEndRef} />
                    </div>

                    {/* Input */}
                    <div className="px-4 py-4 bg-white border-t border-slate-100">
                      <div className="flex gap-3 items-end">
                        <textarea
                          data-testid={AVA.messageInput}
                          value={inputValue}
                          onChange={e => setInputValue(e.target.value)}
                          onKeyDown={handleKeyDown}
                          placeholder="Type your message..."
                          rows={1}
                          disabled={isTyping}
                          className="flex-1 px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 disabled:opacity-50"
                          style={{ maxHeight: 120 }}
                        />
                        <button
                          data-testid={AVA.sendButton}
                          onClick={sendMessage}
                          disabled={isTyping || !inputValue.trim()}
                          className="w-11 h-11 rounded-full bg-blue-600 flex items-center justify-center hover:bg-blue-700 transition-all duration-200 disabled:opacity-40 flex-shrink-0">
                          {isTyping ? <Loader2 size={18} className="text-white animate-spin" /> : <Send size={18} className="text-white" />}
                        </button>
                      </div>
                      <p className="text-xs text-slate-400 text-center mt-2">
                        Apexum does not provide medical advice or prescribe medication.
                      </p>
                    </div>
                  </div>
                )}
              </motion.div>
            ) : activeTab === "now" ? (
              <motion.div key="now" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}>
                <div className="bg-white rounded-3xl border border-slate-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] p-8">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="relative">
                      <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-blue-600 to-cyan-500 flex items-center justify-center shadow-md shadow-blue-600/20">
                        <PhoneCall size={20} className="text-white" />
                      </div>
                      <span className="absolute -top-1 -right-1 w-3.5 h-3.5 rounded-full bg-emerald-400 border-2 border-white animate-pulse" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Have AvA Call You Now</h3>
                      <p className="text-sm text-slate-500">AvA will call you within moments — no scheduling needed.</p>
                    </div>
                  </div>
                  <CallNowForm />
                </div>
              </motion.div>
            ) : (
              <motion.div key="call" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}>
                <div className="bg-white rounded-3xl border border-slate-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] p-8">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 rounded-2xl bg-blue-50 flex items-center justify-center">
                      <Phone size={22} className="text-blue-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Request a Call from AvA</h3>
                      <p className="text-sm text-slate-500">AvA will call you to complete your intake by phone.</p>
                    </div>
                  </div>
                  <CallRequestForm />
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </section>

      <Footer />
    </div>
  );
}
